import Button from "@/Components/Button/Button";
import InputColor from "@/Components/Input/InputColor";
import RadioButtonGroup from "@/Components/Input/RadioButtonGroup";
import Select from "@/Components/Select/Select";
import useIsMobileHook from "@/shared/hooks/useIsMobileHook";
import { posDeliveryMethods, posMenuColors } from "@/shared/utils/constant";
import { None } from "@/shared/utils/enum";
import { hexPattern } from "@/shared/utils/patterns";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import SizeSelector from "../../../../Components/SizeSelector/index.js";
import BackButton from "@/Components/Button/BackButton";
import { useSetPosShowSettings } from "@/shared/atoms/usePosShowSettings.js";
import usePosSettingsPaymentOptions from "@/shared/hooks/usePosSettingsPaymentOptions.js";
import Icon from "@/Components/Icon/Icon.js";
import useCategoryQueryHook from "@/shared/hooks/useCategoryQueryHook.js";
import { getFieldByLang } from "@/shared/utils/helpers.js";
import Textfield from "@/Components/Input/Textfield.js";
import Center from "@/Components/Base/Center.js";
import { ColumnCountPerCategoryArrayItem } from "@/shared/queries/pos/usePosUserSettingsQuery.js";
import usePosUserSettingsQuery from "@/shared/queries/pos/usePosUserSettingsQuery.js";
import usePosUserSettingsPost from "@/shared/mutations/pos/usePosUserSettingsPost.js";
import usePosOrderStandardHook from "@/shared/hooks/usePosOrderStandardHook.js";
import usePosOrderCartWidthAtom from "@/shared/atoms/usePosOrderCartWidthAtom.js";
import { arrayMove } from "react-sortable-hoc";
import SortableSelect from "../../../../Components/Select/SortableSelect";
import useGetTerminals from "@/shared/queries/pos/useGetTerminals.js";
import { posTableDisplaysOptions } from "@/shared/atoms/usePosTableFilter";

const PosOrderUserSettings = () => {
  const { t } = useTranslation();
  const none = {
    translationKey: "none",
    value: None.NONE,
    label: t("posOrders.none"),
  };
  const setShowSettings = useSetPosShowSettings();
  const mutation = usePosUserSettingsPost();
  const { data: settings, refetch: refetchSettings } =
    usePosUserSettingsQuery();
  const { data: terminals = [], isPending: isTerminalsPending } =
    useGetTerminals();
  const isMobile = useIsMobileHook();
  const [colorPalette, setColorPalette] = useState([...settings.color_palette]);
  const { setValue, register, errors, handleSubmit, control, watch } = useForm({
    mode: "onChange",
  });
  const paymentMethodOptions = watch("payment_method_options");
  const standardPaymentMethod = watch("standard_payment_method");
  const standardMenuTarget = watch("standard_get_menu_target");
  const deliveryMethodOptions = watch("delivery_method_options");
  const posSettingPaymentOptions = usePosSettingsPaymentOptions();

  const selectedPaymentMethods = (
    paymentMethodOptions || settings.payment_method_options
  ).filter((paymentId) =>
    posSettingPaymentOptions.some(({ value }) => value === paymentId)
  );
  const selectedDeliveryMethods = posDeliveryMethods.filter(({ value }) =>
    deliveryMethodOptions?.includes(value)
  );
  const [columnCountPerCategory, setColumnCountPerCategory] = useState<
    ColumnCountPerCategoryArrayItem[]
  >([]);
  const { categories } = useCategoryQueryHook();
  const { setStandardDeliveryMethod, setStandardPaymentMethod } =
    usePosOrderStandardHook();
  const [posOrderCartWidth, setPosOrderCartWidth] = usePosOrderCartWidthAtom();

  useEffect(() => {
    if (settings.column_count_per_category) {
      setColumnCountPerCategory(settings.column_count_per_category_array);
    }
  }, [
    settings?.column_count_per_category_array,
    settings?.column_count_per_category,
  ]);

  useEffect(() => {
    if (
      selectedPaymentMethods &&
      standardPaymentMethod &&
      paymentMethodOptions.includes(standardPaymentMethod) === false
    ) {
      setValue("standard_payment_method", None.NONE);
    }
  }, [selectedPaymentMethods]);

  useEffect(() => {
    if (selectedDeliveryMethods) {
      if (
        standardMenuTarget &&
        deliveryMethodOptions.includes(standardMenuTarget) === false
      ) {
        setValue("standard_get_menu_target", None.NONE);
      }
    }
  }, [selectedDeliveryMethods]);

  const onSubmit = (data) => {
    for (let i = 0; i < colorPalette.length; i++) {
      delete data[`color${i}`];
    }

    data.column_count_per_category = columnCountPerCategory.reduce(
      (acc, curr) => {
        curr.categoryId.forEach((id) => (acc[id] = curr.columnCount));
        return acc;
      },
      {}
    );

    const submitData = {
      ...data,
      color_palette: colorPalette,
      is_cart_on_right: Boolean(data.is_cart_on_right),
      is_always_ask_for_topping: Boolean(data.is_always_ask_for_topping),
      show_print_popup: Boolean(data.show_print_popup),
      is_map_view: Boolean(data.is_map_view),
    };

    mutation.mutateAsync(submitData).then(() => {
      refetchSettings().then(({ data }) => {
        setStandardDeliveryMethod(false, data?.standard_delivery_method);
        setStandardPaymentMethod(undefined, data?.standard_payment_method);
      });
      setShowSettings(false);
    });
  };

  const handleSetColor = (
    color: string,
    index: number,
    defColorPalette?: string[]
  ) => {
    setValue(`color${index}`, color);
    const newColors = [...(defColorPalette ? defColorPalette : colorPalette)];
    newColors[index] = color;
    setColorPalette(newColors);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-0">
      <div className="mb-4 flex items-center justify-between">
        <BackButton onClick={() => setShowSettings(false)}>
          {t("back")}
        </BackButton>
        <Button isPending={mutation.isPending}>{t("posOrders.save")}</Button>
      </div>

      <div className="mb-4 flex flex-col gap-4 rounded-2xl bg-white p-8">
        <Controller
          defaultValue={settings.default_pos_terminal_id ?? null}
          name="default_pos_terminal_id"
          control={control}
          render={({ value, onChange }) => (
            <div className="w-full">
              <Select
                label={t("posOrders.posTerminal")}
                placeholder={t("posOrders.selectPosTerminal")}
                value={
                  terminals.find(
                    ({ pos_terminal_id }) => pos_terminal_id === value
                  ) ?? null
                }
                options={terminals}
                isLoading={isTerminalsPending}
                getOptionLabel={(option) => option.pos_terminal_name}
                getOptionValue={(option) => option.pos_terminal_id}
                onChange={(v) => onChange(v?.pos_terminal_id ?? null)}
                menuPortalTarget={document.getElementById("root")}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 10000000000 }),
                }}
              />
            </div>
          )}
        />

        <Controller
          defaultValue={
            settings.delivery_method_options ??
            posDeliveryMethods.map(({ value }) => value)
          }
          name="delivery_method_options"
          control={control}
          rules={{
            validate: (value = []) => {
              return value.length > 0 || t("posOrders.validationPaymentMethod");
            },
          }}
          render={({ onChange }) => {
            return (
              <div className="w-full">
                <Select
                  label={t("posOrders.deliveryMethodOptions")}
                  onChange={(v) => onChange(v.map(({ value }) => value).flat())}
                  value={selectedDeliveryMethods}
                  options={posDeliveryMethods}
                  getOptionLabel={(option) => t(`${option.translationKey}`)}
                  isMulti
                  error={errors?.delivery_method_options?.message}
                />
              </div>
            );
          }}
        />
        <Controller
          defaultValue={settings.is_map_view ?? true}
          name="is_map_view"
          control={control}
          render={({ value, onChange }) => (
            <Select
              label={t("display")}
              options={posTableDisplaysOptions}
              getOptionLabel={(option) => t(option.label)}
              getOptionValue={(option) => option.value}
              value={posTableDisplaysOptions.find(
                (option) => option.value === value
              )}
              onChange={(option) => onChange(option.value)}
            />
          )}
        />
        <Controller
          defaultValue={settings.standard_get_menu_target}
          name="standard_get_menu_target"
          control={control}
          direction={isMobile ? "column" : "row"}
          render={({ value, onChange }) => (
            <RadioButtonGroup
              direction={isMobile ? "column" : "row"}
              label={t("posOrders.standardMenuTarget")}
              onChange={(e) => onChange(Number(e.target.value))}
              value={value}
              options={[none, ...selectedDeliveryMethods].map((i) => ({
                value: i.value,
                label: t(`${i.translationKey}`),
              }))}
            />
          )}
        />

        <Controller
          defaultValue={settings.standard_delivery_method}
          name="standard_delivery_method"
          control={control}
          direction={isMobile ? "column" : "row"}
          rules={{
            validate: (value: number) => {
              if (value === 0 || !deliveryMethodOptions.includes(value)) {
                return t("fieldRequired");
              }
              return true;
            },
          }}
          render={({ value, onChange }) => (
            <RadioButtonGroup
              direction={isMobile ? "column" : "row"}
              label={t("posOrders.standardDeliveryMethod")}
              onChange={(e) => onChange(Number(e.target.value))}
              value={value}
              options={selectedDeliveryMethods.map((i) => ({
                value: i.value,
                label: t(`${i.translationKey}`),
              }))}
              error={errors?.standard_delivery_method?.message}
            />
          )}
        />

        <Controller
          defaultValue={
            settings.payment_method_options ??
            posSettingPaymentOptions.map(({ value }) => value)
          }
          name="payment_method_options"
          control={control}
          rules={{
            validate: (value = []) => {
              return value.length > 0 || t("posOrders.validationPaymentMethod");
            },
          }}
          render={({ onChange, value }) => {
            return (
              <SortableSelect
                onSortEnd={({ oldIndex, newIndex }) => {
                  const newValue = arrayMove(value, oldIndex, newIndex);
                  onChange(newValue);
                }}
                isMulti
                label={t("posOrders.paymentMethodOptions")}
                value={selectedPaymentMethods.map((paymentId) =>
                  posSettingPaymentOptions.find(
                    ({ value }) => value === paymentId
                  )
                )}
                onChange={(v) => onChange(v.map(({ value }) => value).flat())}
                options={posSettingPaymentOptions}
                error={errors?.payment_method_options?.message}
              />
            );
          }}
        />

        <Controller
          defaultValue={settings.standard_payment_method}
          name="standard_payment_method"
          control={control}
          direction={isMobile ? "column" : "row"}
          render={({ value, onChange }) => (
            <RadioButtonGroup
              label={t("posOrders.standardPaymentMethod")}
              onChange={(e) => onChange(Number(e.target.value))}
              value={value}
              options={[
                none,
                ...selectedPaymentMethods.map((paymentId) =>
                  posSettingPaymentOptions.find((v) => v.value === paymentId)
                ),
              ]}
            />
          )}
        />

        <div className="flex gap-12">
          <Controller
            defaultValue={settings.category_card_position}
            name="category_card_position"
            control={control}
            render={({ value, onChange }) => (
              <RadioButtonGroup
                label={t("posOrders.categoryCardPosition")}
                onChange={(e) => onChange(Number(e.target.value))}
                value={value}
                options={[
                  { value: 1, label: t("posOrders.standard") },
                  { value: 2, label: t("posOrders.left") },
                  { value: 3, label: t("posOrders.top") },
                ]}
              />
            )}
          />
          <Controller
            defaultValue={settings.is_cart_on_right ? 1 : 0}
            name="is_cart_on_right"
            control={control}
            render={({ value, onChange }) => (
              <RadioButtonGroup
                label={t("posOrders.cartPosition")}
                onChange={(e) => onChange(Number(e.target.value))}
                value={value}
                options={[
                  { value: 0, label: t("posOrders.left") },
                  { value: 1, label: t("posOrders.right") },
                ]}
              />
            )}
          />
          <Controller
            defaultValue={settings.is_always_ask_for_topping ? 1 : 0}
            name="is_always_ask_for_topping"
            control={control}
            render={({ value, onChange }) => (
              <RadioButtonGroup
                label={t("posOrders.isAlwaysAskForTopping")}
                onChange={(e) => onChange(Number(e.target.value))}
                value={value}
                options={[
                  { value: 1, label: t("yes") },
                  { value: 0, label: t("no") },
                ]}
              />
            )}
          />
          <Controller
            defaultValue={settings.show_print_popup ? 1 : 0}
            name="show_print_popup"
            control={control}
            render={({ value, onChange }) => (
              <RadioButtonGroup
                label={t("posOrders.showPrintPopup")}
                onChange={(e) => onChange(Number(e.target.value))}
                value={value}
                options={[
                  { value: 1, label: t("yes") },
                  { value: 0, label: t("no") },
                ]}
              />
            )}
          />
        </div>

        <div>
          <SizeSelector
            fontSize={posOrderCartWidth}
            setFontSize={setPosOrderCartWidth}
            label={t("posOrders.cartWidth")}
            sizes={[15, 20, 25, 30, 40]}
            unit="%"
          />
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-4 rounded-2xl bg-white p-8">
        <Controller
          defaultValue={settings.font_size ?? 16}
          name="font_size"
          control={control}
          render={({ value, onChange }) => (
            <SizeSelector
              fontSize={value}
              setFontSize={onChange}
              label={t("posOrders.fontSize")}
            />
          )}
        />

        <Controller
          defaultValue={settings.category_column_count || 0}
          name="category_column_count"
          control={control}
          render={({ value, onChange }) => (
            <RadioButtonGroup
              label={t("posOrders.categoryColumnCount")}
              onChange={(e) => onChange(Number(e.target.value))}
              direction={isMobile ? "column" : "row"}
              value={value}
              options={[
                { value: 1, label: t("posOrders.columnCount", { value: 1 }) },
                {
                  value: 2,
                  label: t("posOrders.columnCounts", { value: 2 }),
                },
                {
                  value: 3,
                  label: t("posOrders.columnCounts", { value: 3 }),
                },
                {
                  value: 4,
                  label: t("posOrders.columnCounts", { value: 4 }),
                },
                {
                  value: 6,
                  label: t("posOrders.columnCounts", { value: 6 }),
                },
                {
                  value: 8,
                  label: t("posOrders.columnCounts", { value: 8 }),
                },
              ]}
            />
          )}
        />

        <Controller
          defaultValue={settings.product_column_count || 0}
          name="product_column_count"
          control={control}
          render={({ value, onChange }) => (
            <RadioButtonGroup
              label={t("posOrders.productColumnCount")}
              onChange={(e) => onChange(Number(e.target.value))}
              direction={isMobile ? "column" : "row"}
              value={value}
              options={[
                { value: 1, label: t("posOrders.columnCount", { value: 1 }) },
                {
                  value: 2,
                  label: t("posOrders.columnCounts", { value: 2 }),
                },
                {
                  value: 3,
                  label: t("posOrders.columnCounts", { value: 3 }),
                },
                {
                  value: 4,
                  label: t("posOrders.columnCounts", { value: 4 }),
                },
                {
                  value: 6,
                  label: t("posOrders.columnCounts", { value: 6 }),
                },
                {
                  value: 8,
                  label: t("posOrders.columnCounts", { value: 8 }),
                },
              ]}
            />
          )}
        />
        <div>
          {Array.from({ length: columnCountPerCategory.length }).map(
            (_, index) => (
              <div
                key={index}
                className="mb-4 flex w-1/2 items-center justify-between gap-4"
              >
                <div className="w-[70%]">
                  <Select
                    value={
                      columnCountPerCategory[index]?.categoryId?.map((id) =>
                        categories.find((c) => c.item_id === id)
                      ) ?? []
                    }
                    options={(() => {
                      const filteredCategories = columnCountPerCategory
                        .map((c) => c.categoryId)
                        .flat();
                      return categories.filter(
                        (c) => !filteredCategories.includes(c.item_id)
                      );
                    })()}
                    menuPortalTarget={document.getElementById("root")}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 10000000000 }),
                    }}
                    getOptionLabel={(option) => getFieldByLang(option.name)}
                    getOptionValue={(option) => option.item_id}
                    isMulti
                    onChange={(v) => {
                      const newValue = [...columnCountPerCategory];
                      newValue[index].categoryId = v.map(
                        ({ item_id }) => item_id
                      );
                      setColumnCountPerCategory(newValue);
                    }}
                  />
                </div>
                <div className="flex w-[30%] gap-4">
                  <Textfield
                    placeholder="count"
                    type="text"
                    pattern="[0-9]*"
                    onChange={(e) => {
                      const newValue = [...columnCountPerCategory];
                      newValue[index].columnCount = Number(e.target.value);
                      setColumnCountPerCategory(newValue);
                    }}
                    defaultValue={
                      columnCountPerCategory[index]?.columnCount || ""
                    }
                  />

                  <Center
                    onClick={() =>
                      setColumnCountPerCategory((prev) => [
                        ...prev.slice(0, index),
                        ...prev.slice(index + 1),
                      ])
                    }
                  >
                    <Icon className="h-8 w-8 fill-error" variants="times" />
                  </Center>
                </div>
              </div>
            )
          )}
          <Button
            style={{ width: "200px" }}
            onClick={() =>
              setColumnCountPerCategory((prev) => [
                ...prev,
                { categoryId: [], columnCount: 0 },
              ])
            }
            type="button"
          >
            {t("posOrders.add")}
          </Button>
        </div>

        <div className="mb-4 flex gap-4 text-[1.8rem] font-semibold">
          {t("posOrders.colorSetting")}
          <span
            onClick={() =>
              posMenuColors.forEach((color, index) =>
                handleSetColor(color, index, posMenuColors)
              )
            }
            className="cursor-pointer text-primary underline"
          >
            {t("posOrders.standardColors")}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {colorPalette.map((color, index) => (
            <div key={index}>
              <InputColor
                value={color}
                name={`color${index}`}
                errors={errors}
                onChange={(color) => handleSetColor(color, index)}
                ref={register({
                  pattern: {
                    value: hexPattern,
                    message: t("posOrders.validationWarningMessage"),
                  },
                })}
              />
            </div>
          ))}
        </div>
      </div>
    </form>
  );
};

export default PosOrderUserSettings;
